import { type ToolCall as AgUiToolCall } from '@ag-ui/client';
import type { CopilotThinkingSignature } from '@precis-digital/kurama/MessageLayout';
import type { Theme } from '@precis-digital/kurama/theme';
import { getInspectCurrentPageThinkingSignature } from './utils/inspectCurrentPageDisplay';
import type { CopilotPageElement } from './pageContext/types';
import { getActionDetailContent } from './toolRegistryPreviews';
import { type CopilotActionDisplayResolvers, type CopilotThinkingTitles, type CopilotToolDefinition, type CopilotToolPresentation, type CopilotVisiblePlacement, getActionLabelFromPresentation } from './toolRegistry/types';
import CopilotActionDetail from './components/CopilotActionDetail';
import { highlightCopilotFrontendTool } from './hooks/useCopilotFrontendToolElementEffects';
import { isRecord, parseToolCallArgumentsForDisplay } from './toolRegistry/parsing';
import { DATA_AGENT_TOOL_REGISTRY } from './dataAgentToolRegistry';
export type { CopilotActionDisplayResolvers } from 'shared/copilot/toolRegistry/types';
export const COPILOT_TOOL_REGISTRY: Record<string, CopilotToolDefinition> = {
  ...DATA_AGENT_TOOL_REGISTRY,
  search_for_information_from_the_public_alvie_help_center: {
    presentation: {
      kind: 'thinking_search',
      thinkingTitles: {
        pastTense: 'Searched help.alvie.io',
        presentTense: 'Searching help.alvie.io'
      },
      getSearchTerm: toolCall => {
        try {
          const args = JSON.parse(toolCall.function.arguments) as Record<string, unknown>;
          return typeof args.query === 'string' && args.query.trim().length > 0 ? args.query : 'help center';
        } catch {
          return 'help center';
        }
      }
    }
  },
  search_for_information_from_the_private_alvie_help_center: {
    presentation: {
      kind: 'thinking_search',
      thinkingTitles: {
        pastTense: 'Searched Teamwork Space',
        presentTense: 'Searching Teamwork Space'
      },
      getSearchTerm: toolCall => {
        try {
          const args = JSON.parse(toolCall.function.arguments) as Record<string, unknown>;
          return typeof args.query === 'string' && args.query.trim().length > 0 ? args.query : 'internal help';
        } catch {
          return 'internal help';
        }
      }
    }
  },
  get_copilot_pro_capabilities: {
    presentation: {
      kind: 'thinking_inspect_source',
      thinkingTitles: {
        pastTense: 'Checked Alvie AI Pro capabilities',
        presentTense: 'Checking Alvie AI Pro capabilities'
      },
      description: 'Alvie AI Pro capabilities'
    }
  },
  inspect_current_page: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Inspected current page',
        presentTense: 'Inspecting current page'
      },
      resolveSignature: getInspectCurrentPageThinkingSignature
    }
  },
  read_budget_optimiser_recommendations: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Read budget optimiser recommendations',
        presentTense: 'Reading budget optimiser recommendations'
      },
      description: 'budget optimiser recommendations'
    }
  },
  list_filter_options: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed filter options',
        presentTense: 'Listing filter options'
      },
      description: 'the available page filters'
    }
  },
  get_recommendation_details: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Read recommendation details',
        presentTense: 'Reading recommendation details'
      },
      description: 'the recommendation rule'
    }
  },
  list_recommendations: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed recommendations',
        presentTense: 'Listing recommendations'
      },
      description: 'matching recommendation rules'
    }
  },
  get_recommendation_advanced_view: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Read recommendation advanced view',
        presentTense: 'Reading recommendation advanced view'
      },
      description: 'the affected entity table'
    }
  },
  list_available_metrics_for_scorecard_group: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Inspected scorecards',
        presentTense: 'Inspecting scorecards'
      },
      description: 'for available metrics'
    }
  },
  list_available_conversion_events: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed available conversion events',
        presentTense: 'Listing available conversion events'
      },
      description: 'this page'
    }
  },
  list_available_progress_over_time_chart_settings: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Inspected settings',
        presentTense: 'Inspecting settings'
      },
      description: 'the progress over time chart'
    }
  },
  list_available_detailed_table_settings: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Inspected settings',
        presentTense: 'Inspecting settings'
      },
      description: 'the detailed report table'
    }
  },
  list_listview_status_tabs: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed status tabs',
        presentTense: 'Listing status tabs'
      },
      description: 'the list status tabs'
    }
  },
  list_listview_filters: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed filters',
        presentTense: 'Listing filters'
      },
      description: 'the list dropdown filters'
    }
  },
  get_listview_search: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Read list search',
        presentTense: 'Reading list search'
      },
      description: 'the list search field'
    }
  },
  get_listview_pagination: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Read pagination',
        presentTense: 'Reading pagination'
      },
      description: 'the list pagination'
    }
  },
  list_recommendation_assignees: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed recommendation assignees',
        presentTense: 'Listing recommendation assignees'
      },
      description: 'workspace members'
    }
  },
  update_date_range: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Changed date range to:',
      thinkingTitles: {
        pastTense: 'Updated date range',
        presentTense: 'Updating date range'
      },
      hasHighlight: true
    }
  },
  set_page_filters: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Applied filters:',
      thinkingTitles: {
        pastTense: 'Updated page filters',
        presentTense: 'Updating page filters'
      },
      hasHighlight: true
    }
  },
  switch_listview_status_tab: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Switched tab to:',
      thinkingTitles: {
        pastTense: 'Switched status tab',
        presentTense: 'Switching status tab'
      },
      hasHighlight: true
    }
  },
  update_listview_filters: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Applied filters:',
      thinkingTitles: {
        pastTense: 'Updated filters',
        presentTense: 'Updating filters'
      },
      hasHighlight: true
    }
  },
  update_listview_search: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Updated search to:',
      thinkingTitles: {
        pastTense: 'Updated list search',
        presentTense: 'Updating list search'
      },
      hasHighlight: true
    }
  },
  set_listview_rows_per_page: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Changed rows per page to:',
      thinkingTitles: {
        pastTense: 'Changed rows per page',
        presentTense: 'Changing rows per page'
      },
      hasHighlight: true
    }
  },
  set_listview_page_number: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Moved list to page:',
      thinkingTitles: {
        pastTense: 'Changed list page',
        presentTense: 'Changing list page'
      },
      hasHighlight: true
    }
  },
  start_create_new: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Started create new:',
      thinkingTitles: {
        pastTense: 'Started create new',
        presentTense: 'Starting create new'
      },
      hasHighlight: true
    }
  },
  start_connect_data_source: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Opened the connect dialog for:',
      thinkingTitles: {
        pastTense: 'Opened the connect data source dialog',
        presentTense: 'Opening the connect data source dialog'
      }
    }
  },
  start_reconnect_data_source: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Opened the reconnect dialog for:',
      thinkingTitles: {
        pastTense: 'Opened the reconnect dialog',
        presentTense: 'Opening the reconnect dialog'
      }
    }
  },
  set_data_source_recommendations: {
    presentation: {
      kind: 'visible',
      title: 'Change recommendation settings',
      visibleRenderKind: 'confirm_action',
      placement: 'message_end'
    }
  },
  delete_data_sources: {
    presentation: {
      kind: 'visible',
      title: 'Delete data sources',
      visibleRenderKind: 'confirm_action',
      placement: 'message_end'
    }
  },
  set_page_view: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Changed view to:',
      thinkingTitles: {
        pastTense: 'Changed page view',
        presentTense: 'Changing page view'
      },
      hasHighlight: true
    }
  },
  set_recommendation_severity: {
    presentation: {
      kind: 'visible',
      title: 'Change recommendation severity',
      visibleRenderKind: 'confirm_action',
      placement: 'message_end'
    }
  },
  set_recommendation_assignee: {
    presentation: {
      kind: 'visible',
      title: 'Change recommendation assignee',
      visibleRenderKind: 'confirm_action',
      placement: 'message_end'
    }
  },
  set_recommendation_status: {
    presentation: {
      kind: 'visible',
      title: 'Change recommendation status',
      visibleRenderKind: 'confirm_action',
      placement: 'message_end'
    }
  },
  edit_recommendation: {
    presentation: {
      kind: 'visible',
      title: 'Edit recommendation',
      visibleRenderKind: 'confirm_action',
      placement: 'message_end'
    }
  },
  show_recommendation_how_to_fix: {
    presentation: {
      kind: 'visible',
      title: 'Open how to fix',
      visibleRenderKind: 'confirm_action',
      placement: 'message_end'
    }
  },
  show_recommendation_advanced_view: {
    presentation: {
      kind: 'visible',
      title: 'Open advanced view',
      visibleRenderKind: 'confirm_action',
      placement: 'message_end'
    }
  },
  select_metrics_in_scorecard_group: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Changed scorecard metrics to:',
      thinkingTitles: {
        pastTense: 'Changed scorecard metrics',
        presentTense: 'Changing scorecard metrics'
      },
      hasHighlight: true
    }
  },
  select_conversion_event: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Selected conversion event:',
      thinkingTitles: {
        pastTense: 'Selected conversion event',
        presentTense: 'Selecting conversion event'
      },
      hasHighlight: true
    }
  },
  update_progress_over_time_chart_settings: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Updated progress over time chart:',
      thinkingTitles: {
        pastTense: 'Updated progress over time chart',
        presentTense: 'Updating progress over time chart'
      },
      hasHighlight: true
    }
  },
  update_detailed_table_settings: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Updated detailed table:',
      thinkingTitles: {
        pastTense: 'Updated detailed report table',
        presentTense: 'Updating detailed report table'
      },
      hasHighlight: true
    }
  },
  list_workspace_members: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed workspace members',
        presentTense: 'Listing workspace members'
      },
      description: 'the workspace members table'
    }
  },
  list_pending_invites: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed pending invites',
        presentTense: 'Listing pending invites'
      },
      description: 'pending workspace invitations'
    }
  },
  switch_workspace_details_tab: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Switched workspace tab to:',
      thinkingTitles: {
        pastTense: 'Switched workspace tab',
        presentTense: 'Switching workspace tab'
      },
      hasHighlight: false
    }
  },
  filter_workspace_members_by_role: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Filtered members by role:',
      thinkingTitles: {
        pastTense: 'Filtered workspace members',
        presentTense: 'Filtering workspace members'
      },
      hasHighlight: true
    }
  },
  invite_workspace_members: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Invited members:',
      thinkingTitles: {
        pastTense: 'Invited workspace members',
        presentTense: 'Inviting workspace members'
      },
      hasHighlight: true
    }
  },
  update_workspace_member_role: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Updated member role:',
      thinkingTitles: {
        pastTense: 'Updated member role',
        presentTense: 'Updating member role'
      },
      hasHighlight: true
    }
  },
  remove_workspace_member: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Removed member:',
      thinkingTitles: {
        pastTense: 'Removed workspace member',
        presentTense: 'Removing workspace member'
      },
      hasHighlight: true
    }
  },
  resend_workspace_invite: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Resent invite:',
      thinkingTitles: {
        pastTense: 'Resent workspace invite',
        presentTense: 'Resending workspace invite'
      },
      hasHighlight: true
    }
  },
  revoke_workspace_invite: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Revoked invite:',
      thinkingTitles: {
        pastTense: 'Revoked workspace invite',
        presentTense: 'Revoking workspace invite'
      },
      hasHighlight: true
    }
  },
  update_workspace_notification_settings: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Updated notification settings:',
      thinkingTitles: {
        pastTense: 'Updated notification settings',
        presentTense: 'Updating notification settings'
      },
      hasHighlight: true
    }
  },
  update_budget_optimiser_ad_spend_target: {
    presentation: {
      kind: 'visible',
      title: 'Updated budget optimiser ad spend target',
      visibleRenderKind: 'generic'
    }
  },
  list_available_leaderboard_views: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed leaderboard views',
        presentTense: 'Listing leaderboard views'
      },
      description: 'Creative Reporting overview leaderboard views'
    }
  },
  update_leaderboard_view: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Changed leaderboard view to:',
      thinkingTitles: {
        pastTense: 'Updated leaderboard view',
        presentTense: 'Updating leaderboard view'
      },
      hasHighlight: true
    }
  },
  list_available_creative_reports: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed creative reports',
        presentTense: 'Listing creative reports'
      },
      description: 'Creative Reporting solutions on this page'
    }
  },
  select_creative_report: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Switched creative report to:',
      thinkingTitles: {
        pastTense: 'Switched creative report',
        presentTense: 'Switching creative report'
      },
      hasHighlight: true
    }
  },
  list_available_dashboards: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed dashboards',
        presentTense: 'Listing dashboards'
      },
      description: 'Saved Creative Reporting dashboards'
    }
  },
  open_dashboard: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Opened dashboard:',
      thinkingTitles: {
        pastTense: 'Opened dashboard',
        presentTense: 'Opening dashboard'
      },
      hasHighlight: true
    }
  },
  set_search: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Updated search to:',
      thinkingTitles: {
        pastTense: 'Updated search',
        presentTense: 'Updating search'
      },
      hasHighlight: true
    }
  },
  list_group_by_options: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed group-by options',
        presentTense: 'Listing group-by options'
      },
      description: 'Creative Reporting dashboard group-by options'
    }
  },
  set_group_by: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Changed group by to:',
      thinkingTitles: {
        pastTense: 'Updated group by',
        presentTense: 'Updating group by'
      },
      hasHighlight: true
    }
  },
  list_available_chart_settings: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Inspected chart settings',
        presentTense: 'Inspecting chart settings'
      },
      description: 'the Creative Reporting dashboard chart'
    }
  },
  update_chart_settings: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Updated chart metrics to:',
      thinkingTitles: {
        pastTense: 'Updated chart settings',
        presentTense: 'Updating chart settings'
      },
      hasHighlight: true
    }
  },
  list_available_table_settings: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Inspected table settings',
        presentTense: 'Inspecting table settings'
      },
      description: 'the Creative Reporting ads detailed report'
    }
  },
  update_table_settings: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Updated table metrics to:',
      thinkingTitles: {
        pastTense: 'Updated table settings',
        presentTense: 'Updating table settings'
      },
      hasHighlight: true
    }
  },
  list_available_chart_types: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed chart types',
        presentTense: 'Listing chart types'
      },
      description: 'Creative Reporting dashboard chart types'
    }
  },
  update_chart_type: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Changed chart type to:',
      thinkingTitles: {
        pastTense: 'Updated chart type',
        presentTense: 'Updating chart type'
      },
      hasHighlight: true
    }
  },
  list_available_chart_ads: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed chart groups',
        presentTense: 'Listing chart groups'
      },
      description: 'Creative Reporting dashboard chart groups'
    }
  },
  open_creative_details: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Opened creative details for:',
      thinkingTitles: {
        pastTense: 'Opened creative details',
        presentTense: 'Opening creative details'
      },
      hasHighlight: true
    }
  },
  update_chart_ads: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Updated chart groups to:',
      thinkingTitles: {
        pastTense: 'Updated chart groups',
        presentTense: 'Updating chart groups'
      },
      hasHighlight: true
    }
  },
  list_available_ads_report_views: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed ads report views',
        presentTense: 'Listing ads report views'
      },
      description: 'Creative Reporting ads detailed report views'
    }
  },
  update_ads_report_view: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Changed ads report view to:',
      thinkingTitles: {
        pastTense: 'Updated ads report view',
        presentTense: 'Updating ads report view'
      },
      hasHighlight: true
    }
  },
  list_dashboard_templates: {
    presentation: {
      kind: 'thinking_inspect_page',
      thinkingTitles: {
        pastTense: 'Listed dashboard templates',
        presentTense: 'Listing dashboard templates'
      },
      description: 'Creative Reporting dashboard templates'
    }
  },
  create_dashboard: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Created dashboard:',
      thinkingTitles: {
        pastTense: 'Created dashboard',
        presentTense: 'Creating dashboard'
      },
      hasHighlight: true
    }
  },
  create_dashboard_from_current_view: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Created dashboard from current view:',
      thinkingTitles: {
        pastTense: 'Created dashboard from current view',
        presentTense: 'Creating dashboard from current view'
      }
    }
  },
  update_dashboard_details: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Updated dashboard details:',
      thinkingTitles: {
        pastTense: 'Updated dashboard details',
        presentTense: 'Updating dashboard details'
      }
    }
  },
  save_dashboard: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Saved dashboard:',
      thinkingTitles: {
        pastTense: 'Saved dashboard',
        presentTense: 'Saving dashboard'
      }
    }
  },
  copy_dashboard: {
    presentation: {
      kind: 'visible',
      title: 'Copy dashboard',
      visibleRenderKind: 'confirm_action',
      placement: 'message_end'
    }
  },
  delete_dashboard: {
    presentation: {
      kind: 'visible',
      title: 'Delete dashboard',
      visibleRenderKind: 'confirm_action',
      placement: 'message_end'
    }
  },
  change_url: {
    presentation: {
      kind: 'visible',
      title: 'Change page',
      visibleRenderKind: 'change_url',
      placement: 'message_end'
    }
  },
  get_navigation_target_details: {
    presentation: {
      kind: 'hidden'
    }
  },
  highlight_element: {
    presentation: {
      kind: 'visible',
      title: 'Highlighted page element',
      visibleRenderKind: 'highlight_element'
    }
  },
  create_project: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Created project:',
      thinkingTitles: {
        pastTense: 'Created project',
        presentTense: 'Creating project'
      }
    }
  },
  navigate_to_project: {
    presentation: {
      kind: 'visible',
      title: 'Open project',
      visibleRenderKind: 'change_url',
      placement: 'message_end'
    }
  },
  update_project: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Updated project',
      thinkingTitles: {
        pastTense: 'Updated project',
        presentTense: 'Updating project'
      }
    }
  },
  download_all_assets_for_a_given_project: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Downloaded all assets for a given project',
      thinkingTitles: {
        pastTense: 'Downloaded all assets for a given project',
        presentTense: 'Downloading all assets for a given project'
      }
    }
  },
  get_project_link: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Got project link',
      thinkingTitles: {
        pastTense: 'Got project link',
        presentTense: 'Getting project link'
      }
    }
  },
  add_project_resource: {
    presentation: {
      kind: 'action_page',
      actionLabel: 'Added resource:',
      thinkingTitles: {
        pastTense: 'Added resource',
        presentTense: 'Adding resource'
      }
    }
  },
  delete_project_resource: {
    presentation: {
      kind: 'visible',
      title: 'Delete resource',
      visibleRenderKind: 'confirm_action',
      placement: 'message_end'
    }
  }
};

/**
 * Looks up the registry entry for a copilot tool by name.
 * Returns undefined for tools that have no presentation or handler metadata.
 */
export const getCopilotToolDefinition = (toolName: string): CopilotToolDefinition | undefined => COPILOT_TOOL_REGISTRY[toolName];

/**
 * Returns the UI presentation config for a registered copilot tool.
 * Drives thinking signatures, action cards, and inline tool-call rendering.
 */
export const getCopilotToolPresentation = (toolName: string): CopilotToolPresentation | undefined => COPILOT_TOOL_REGISTRY[toolName]?.presentation;
const warnedUnhandledToolNames = new Set<string>();

/**
 * Warn once per process when a tool appears in a chat trace without a registry
 * entry. Intentionally hidden tools must use `kind: 'hidden'` so they do not
 * trip this — that way new agent tools surface loudly until we map them.
 */
export const noteUnhandledCopilotTool = (toolName: string): void => {
  if (toolName.trim().length === 0) return;
  if (COPILOT_TOOL_REGISTRY[toolName] != null) return;
  if (warnedUnhandledToolNames.has(toolName)) return;
  warnedUnhandledToolNames.add(toolName);
  // eslint-disable-next-line no-console -- intentional once-per-process warning for missing registry entries
  console.warn(`[copilot] Unhandled tool "${toolName}". Add it to COPILOT_TOOL_REGISTRY ` + `(or DATA_AGENT_TOOL_REGISTRY) with a presentation kind, or mark it ` + `kind: 'hidden' if it should stay silent.`);
};

/** @internal Reset warn-once set — for unit tests only. */
export const resetUnhandledCopilotToolWarningsForTests = (): void => {
  warnedUnhandledToolNames.clear();
};
/**
 * Returns whether the tool is intentionally silent in the Alvie transcript.
 */
export const isCopilotHiddenTool = (toolName: string): boolean => getCopilotToolPresentation(toolName)?.kind === 'hidden';

/**
 * Returns whether the tool renders as a user-visible action card in the message stream.
 * Action tools show what changed on the page after the agent mutates UI state.
 */
export const isCopilotActionTool = (toolName: string): boolean => getCopilotToolPresentation(toolName)?.kind === 'action_page';

/**
 * Returns whether the tool renders as a data-agent fetch card in the thinking accordion.
 */
export const isCopilotDataFetchTool = (toolName: string): boolean => getCopilotToolPresentation(toolName)?.kind === 'action_data_fetch';

/**
 * Returns whether the tool renders as a data-agent analysis card in the thinking accordion.
 */
export const isCopilotDataAnalysisTool = (toolName: string): boolean => getCopilotToolPresentation(toolName)?.kind === 'action_data_analysis';

/**
 * Returns whether the tool renders as any data-agent step card in the thinking accordion.
 */
export const isCopilotDataActionTool = (toolName: string): boolean => isCopilotDataFetchTool(toolName) || isCopilotDataAnalysisTool(toolName);

/**
 * Returns whether the tool renders as an inline visible tool-call card.
 * Used for navigation and highlight tools that appear directly in the transcript.
 */
export const isCopilotVisibleTool = (toolName: string): boolean => getCopilotToolPresentation(toolName)?.kind === 'visible';

/**
 * Returns where a visible tool-call card should render in the copilot bubble.
 * Defaults to `inline` (stream order). `message_end` tools are appended after
 * thinking signatures and assistant text when the bubble flushes.
 */
export const getCopilotVisibleToolPlacement = (toolName: string): CopilotVisiblePlacement => {
  const presentation = getCopilotToolPresentation(toolName);
  if (presentation?.kind !== 'visible') return 'inline';
  return presentation.placement ?? 'inline';
};

/**
 * Returns whether the tool contributes a thinking signature in the copilot transcript.
 * Includes action, search, and inspect tools shown while the agent is working.
 */
export const isCopilotThinkingTool = (toolName: string): boolean => {
  const presentation = getCopilotToolPresentation(toolName);
  return presentation?.kind === 'action_page' || presentation?.kind === 'action_data_fetch' || presentation?.kind === 'action_data_analysis' || presentation?.kind === 'thinking_search' || presentation?.kind === 'thinking_inspect_page' || presentation?.kind === 'thinking_inspect_source';
};

/**
 * Returns the action card label for an action-type copilot tool.
 * Resolved from the tool registry presentation config; pass args when labels depend on them.
 */
export const getActionLabel = (toolName: string, args?: Record<string, unknown>): string => getActionLabelFromPresentation(getCopilotToolPresentation(toolName), args);

/**
 * Delegates to preview renderers to build action detail content for the tool registry.
 * Page-specific label resolvers are passed through for translated metric names.
 */
export const getActionDetailContentForRegistry = (toolName: string, args: Record<string, unknown> | undefined, resolvers: CopilotActionDisplayResolvers = {}): ReturnType<typeof getActionDetailContent> => getActionDetailContent(toolName, args, resolvers);

/**
 * Builds a thinking-signature page action card for completed or in-progress page mutation tools.
 * Optionally wires a highlight callback when the tool affected a registered page element.
 */
export const buildPageActionSignature = (toolCall: AgUiToolCall, hasResult: boolean, theme: Theme): CopilotThinkingSignature => {
  const toolName = toolCall.function.name;
  const args = parseToolCallArgumentsForDisplay(toolCall.function.arguments);
  const argsRecord = isRecord(args) ? args : undefined;
  const presentation = getCopilotToolPresentation(toolName);
  const canHighlight = presentation?.kind === 'action_page' && presentation.hasHighlight === true;
  return {
    id: toolCall.id,
    type: 'action',
    label: getActionLabel(toolName, argsRecord),
    detail: <CopilotActionDetail toolName={toolName} args={args} />,
    isInProgress: !hasResult,
    ...(canHighlight ? {
      onHighlight: () => {
        highlightCopilotFrontendTool(toolName, toolCall.id, theme);
      }
    } : {})
  };
};

/**
 * Builds a thinking-signature card for API report fetches.
 */
export const buildDataFetchSignature = (toolCall: AgUiToolCall, hasResult: boolean): CopilotThinkingSignature => {
  const toolName = toolCall.function.name;
  const args = parseToolCallArgumentsForDisplay(toolCall.function.arguments);
  const argsRecord = isRecord(args) ? args : undefined;
  return {
    id: toolCall.id,
    type: 'data_fetch',
    label: getActionLabel(toolName, argsRecord),
    detail: <CopilotActionDetail toolName={toolName} args={args} />,
    isInProgress: !hasResult
  };
};

/**
 * Builds a thinking-signature card for dataset analysis steps.
 */
export const buildDataAnalysisSignature = (toolCall: AgUiToolCall, hasResult: boolean): CopilotThinkingSignature => {
  const toolName = toolCall.function.name;
  const args = parseToolCallArgumentsForDisplay(toolCall.function.arguments);
  const argsRecord = isRecord(args) ? args : undefined;
  return {
    id: toolCall.id,
    type: 'data_analysis',
    label: getActionLabel(toolName, argsRecord),
    detail: <CopilotActionDetail toolName={toolName} args={args} />,
    isInProgress: !hasResult
  };
};
const EMPTY_DESCRIPTION = '';
const getThinkingTitle = (hasResult: boolean, thinkingTitles: CopilotThinkingTitles): string => hasResult ? thinkingTitles.pastTense : thinkingTitles.presentTense;
type ThinkingSearchPresentation = Extract<CopilotToolPresentation, {
  kind: 'thinking_search';
}>;
type ThinkingInspectPagePresentation = Extract<CopilotToolPresentation, {
  kind: 'thinking_inspect_page';
}>;
type ThinkingInspectSourcePresentation = Extract<CopilotToolPresentation, {
  kind: 'thinking_inspect_source';
}>;
const buildSearchThinkingSignature = (toolCall: AgUiToolCall, hasResult: boolean, presentation: ThinkingSearchPresentation, toolResultContent?: string): CopilotThinkingSignature => {
  const title = getThinkingTitle(hasResult, presentation.thinkingTitles);
  const searchDetails = hasResult ? presentation.getSearchDetails?.(toolCall, toolResultContent) : undefined;
  const hasSearchDetails = searchDetails != null && searchDetails.length > 0;
  return {
    id: toolCall.id,
    type: 'search',
    searchTerm: presentation.getSearchTerm(toolCall),
    title,
    ...(hasSearchDetails ? {
      searchDetails
    } : {})
  };
};
const buildInspectPageThinkingSignature = (toolCall: AgUiToolCall, hasResult: boolean, presentation: ThinkingInspectPagePresentation, pageElements: CopilotPageElement[] | undefined): CopilotThinkingSignature => {
  const title = getThinkingTitle(hasResult, presentation.thinkingTitles);
  const resolvedSignature = presentation.resolveSignature?.(toolCall, hasResult, pageElements);
  if (resolvedSignature != null) {
    return {
      id: toolCall.id,
      type: 'inspect_page',
      title: resolvedSignature.title,
      description: resolvedSignature.description
    };
  }
  return {
    id: toolCall.id,
    type: 'inspect_page',
    title,
    description: presentation.description ?? EMPTY_DESCRIPTION
  };
};
const buildInspectSourceThinkingSignature = (toolCall: AgUiToolCall, hasResult: boolean, presentation: ThinkingInspectSourcePresentation): CopilotThinkingSignature => {
  const thinkingTitles = presentation.getThinkingTitles?.(toolCall) ?? presentation.thinkingTitles;
  const title = getThinkingTitle(hasResult, thinkingTitles);
  const description = presentation.getDescription?.(toolCall) ?? presentation.description ?? EMPTY_DESCRIPTION;
  return {
    id: toolCall.id,
    type: 'inspect_source',
    title,
    description
  };
};

/**
 * Maps any registered thinking tool call to its Kurama thinking signature representation.
 * Returns null when the tool has no thinking presentation or is not a thinking tool.
 */
export const extractThinkingSignatureFromToolCall = (toolCall: AgUiToolCall, hasResult: boolean, pageElements: CopilotPageElement[] | undefined, theme: Theme, toolResultContent?: string): CopilotThinkingSignature | null => {
  const presentation = getCopilotToolPresentation(toolCall.function.name);
  if (presentation == null || !isCopilotThinkingTool(toolCall.function.name)) {
    return null;
  }
  if (presentation.kind === 'thinking_search') {
    return buildSearchThinkingSignature(toolCall, hasResult, presentation, toolResultContent);
  }
  if (presentation.kind === 'thinking_inspect_page') {
    return buildInspectPageThinkingSignature(toolCall, hasResult, presentation, pageElements);
  }
  if (presentation.kind === 'thinking_inspect_source') {
    return buildInspectSourceThinkingSignature(toolCall, hasResult, presentation);
  }
  if (presentation.kind === 'action_page') {
    return buildPageActionSignature(toolCall, hasResult, theme);
  }
  if (presentation.kind === 'action_data_fetch') {
    return buildDataFetchSignature(toolCall, hasResult);
  }
  if (presentation.kind === 'action_data_analysis') {
    return buildDataAnalysisSignature(toolCall, hasResult);
  }
  return null;
};
export { DateRangePreview, FilterChipsPreview, getActionDetailContent } from './toolRegistryPreviews';
export { getToolFilterValues } from './toolRegistryPreviews';
export { isRecord, parseToolCallArgumentsForDisplay, parseToolCallArgumentsRecordForDisplay } from './toolRegistry/parsing';